import {subDays} from 'date-fns'
import { isOccasion as isOccasionService } from './serviceUtil';
import { get as _get } from 'lodash';

import {
  maskMoney,
  maskServiceDate,
  maskServiceStartTime,
  portionSizeSwitch,
  serviceTypeSwitch
} from '../masks';
import { formatStartAndEndTime } from './util';

export const generateOccasionMenuEmail = menu => {
  let emailBody = '';
  // TEMPLATE TEXT
  emailBody +=
    '<p>Please let me know if you have any questions.</p>' +
    '<p>xJill & Tiana</p>';
  emailBody += `<p><br>-----------<br></p>`;

  // MENU
  emailBody += generateMenuBody(menu);
  emailBody += `<p><br>-----------<br></p>`;

  return emailBody === '' ? '<p></p>' : emailBody;
};

export const generateServiceDetailEmail = (audience, service) => {
  let emailBody = '';
  // TEMPLATE TEXT
  emailBody +=
    audience === 'staff'
      ? generateChefServiceConfirmationIntro(service)
      : generateClientServiceConfirmationIntro(service);
  emailBody += `<p><br>-----------<br></p>`;

  // SERVICE DETAILS
  emailBody += generateServiceDetails(audience, service);
  emailBody += `<p><br>-----------<br></p>`;

  // CHEF/CLIENT DETAILS
  emailBody += generateUser(audience, service);
  emailBody += `<p><br>-----------<br></p>`;

  // MENU
  emailBody += generateMenuSelectionsBody(service);
  emailBody += generateServiceSpecialInstructions(service);
  emailBody += `<p><br>-----------<br></p>`;

  emailBody +=
    audience === 'staff'
      ? generateTextArea('Service Notes', service.notes)
      : '';
  emailBody += service.client
    ? generateTextArea(null, service.client.client_101)
    : '';

  if(isOccasionService(service)){
    emailBody += 
      audience === 'staff' ? generateChefNoteForOccasions(service) : generateClientNoteForOccasions(service)
  }

  emailBody += '<p>Best,<br>The Culinistas</p>'

  return emailBody === '' ? '<p></p>' : emailBody;
};

export const generateStaffServiceDetailEmail = (staff, service) => {

  let emailBody = '';

  let clientName = ''
  if (service.client) {
    clientName = service.client.first_name + " " + service.client.last_name
  }
  
  let name = _get(staff, 'chef.first_name', '');
  emailBody += `<p>Hello ${name},</p>`
  if (service.service_type === 'custom_weekly') {
    emailBody += '<p>We are checking in to confirm your custom service. Please accept or decline based on what you\'ve confirmed with your client.<p>'
  } else {
    emailBody +=
      '<p>We have a client with whom we\'d like to match you. Please take a look at the details below.</p>';
  }
  emailBody += `<p><b>Client:</b> ${clientName}</p>`;
  emailBody += `<p><b>Date:</b> ${maskServiceDate(service.date)}</p>`;
  if (isOccasionService(service) && service.start_time && service.dinner_start_time) {
    emailBody += `<p><b>Appetizer Time:</b> ${maskServiceStartTime(service.start_time)}</p>`;
    emailBody += `<p><b>Dinner Time:</b> ${maskServiceStartTime(service.dinner_start_time)}</p>`;
    emailBody += `<p><b>End Time:</b> ${maskServiceStartTime(service.end_time)}</p>`;
  } else {
    const time = formatStartAndEndTime(service.start_time, service.end_time);
    emailBody += `<p><b>Time:</b> ${time}</p>`;
  }
  emailBody += `<p><b>Location:</b> ${service.address && service.address.zip_code}</p>`;
  emailBody += `<p><b>Type:</b> ${service.service_type}</p>`;

  if (service.service_type === "weekly" || service.service_type === "custom_weekly") {
    emailBody += `<p><b>Portion Size:</b> ${service.portion_size }</p>`;
  }

  if (service.service_type === "occasion") {
    emailBody += `<p><b>Rate:</b> ${maskMoney(+staff.rate + staff.tip_cents)} (${maskMoney(staff.rate)} + ${maskMoney(staff.tip_cents)} Tip)</p>`;
  } else {
    emailBody += `<p><b>Rate:</b> ${maskMoney(+staff.rate + service.tip_cents)} (${maskMoney(staff.rate)} + ${maskMoney(service.tip_cents)} Tip)</p>`;
  }

  if (service.service_type === "occasion") {
    emailBody += `<p><b>Guest Count:</b> ${service.guest_number }</p>`;
  }

  if (staff.role) {
    emailBody += `<p><b>Role:</b> ${staff.role }</p>`;
  }
  emailBody += `<p><br>-----------<br></p>`;

  return emailBody === '' ? '<p></p>' : emailBody;
};

const generateMenuBody = menu => {
  let copy = '<p><strong>Menu</strong><br></p>';
  if (!menu || !menu.courses.length > 0) return copy;

  copy += menu.courses
    .map(course => {
      let { name: courseName = '' } = course;
      // conditionally write course name + description
      courseName += course.description ? ` (${course.description})` : '';
      courseName = courseName ? `<p><strong>${courseName}</strong></p>` : '';

      // conditionally write dish name + ingredients
      const dishes = course.dishes
        .map(dish => {
          const { name, child_name, public_ingredients } = dish;
          // use child_name over name
          const dishName =
            child_name && child_name.trim()
              ? `<p>${child_name}</p>`
              : name
              ? `<p>${name}</p>`
              : '';
          const ingredients = public_ingredients
            ? `<p><em>${public_ingredients}</em></p>`
            : '';
          return dishName + ingredients;
        })
        .join('');

      return courseName + dishes;
    })
    .join('<p><br></p>');

  return copy;
};

const generateServiceDetails = (audience, service) => {
  let copy = '<p><strong>Service Details</strong></p>';

  copy += service.start_time
    ? `<p>Date: ${maskServiceDate(service.start_time)}</p>`
    : ''; // date

  if (isOccasionService(service) && service.dinner_start_time && service.start_time) {
    copy += `<p>Appetizer Time: ${maskServiceStartTime(service.start_time)}</p>`
    copy += `<p>Dinner Time: ${maskServiceStartTime(service.dinner_start_time)}</p>`
  } else {
    copy += service.start_time
      ? `<p>Time: ${maskServiceStartTime(service.start_time)}</p>`
      : ''; // start time
  }

  copy += service.address
    ? `<p>Location: ${generateAddress(service.address)}</p>`
    : ''; // address
  copy +=
    audience === 'staff' && service.service_type
      ? `<p>Type: ${serviceTypeSwitch(service.service_type)}</p>`
      : ''; // type: service_type
  copy += generateServingSize(service); // Portion Size / Occasion Type + Num Guests
  copy += generateAllergens(service.client || {}); // client allergies
  copy += generateDiet(service.client || {}); // client diet
  copy += generateSpecialInstructions(service.client || {}); // client special instructions

  return copy;
};

const generateServiceSpecialInstructions = service => {
  const { special_instructions } = service;
  // add p tag for each line (from admin textarea input)
  return special_instructions
    ? special_instructions
        .split('\n')
        .map(item => `<p>${item}</p>`)
        .join('')
    : '';
};

const prepCourses = courses => [
  ...courses
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(course => {
      return { ...course, dishes: [] };
    })
];

const mapMenu = ({ menu, menu_selections }) => {
  const courses = prepCourses(menu && 'courses' in menu ? menu.courses : []);
  const course_names = courses.map(course => course.name);

  menu_selections = menu_selections || []

  courses.forEach(course => {
    course.dishes = menu_selections.filter(
      item => item.course_name === course.name
    );
  });
  courses.push({
    sort_order: courses.length,
    name: 'Manually Added',
    dishes: [
      ...menu_selections.filter(
        item => !course_names.includes(item.course_name)
      )
    ]
  });

  return courses;
};

const generateMenuSelectionsBody = service => {
  if (service.service_type === 'custom_weekly')
    return generateTextArea('Menu', service.custom_weekly_details || '');

  const courses = mapMenu(service);
  let copy = '<p><strong>Menu</strong></p>';
  if (courses.length === 0) return copy;

  copy += courses
    .map(course => {
      if (course.dishes.length === 0) return '';

      let body = ''
      body += course.dishes
        .map(selection => {
          const { dish, notes } = selection;
          if (!dish) return '';

          const dishName =
            dish.child_name && dish.child_name.trim()
              ? dish.child_name
              : dish.name;
          const dishNotes = notes ? `<br><em>${notes || ''}</em>` : '';
          return `<p>${dishName} ${dishNotes}</p>`;
        })
        .join('');

      return body;
    })
    .join('');
  return copy;
};

const generateTextArea = (heading, body) => {
  if (heading) {
    return body ? `<p><strong>${heading}</strong></p><p>${body}</p>` : '';
  } else {
    return body
  }
};

const generateAllergens = ({ allergen_tags = [], other_allergens }) => {
  const allergenTags = [...allergen_tags];
  if (other_allergens) allergenTags.push({ tag: other_allergens });

  if (!allergenTags.length) return '';
  return `<p>Allergens: ${allergenTags
    .map(allergy => allergy.tag)
    .join(', ')}</p>`;
};

const generateDiet = ({ dietary_preference_tags: dietTags = [] }) => {
  if (!dietTags.length) return '';
  return `<p>Diet: ${dietTags.map(allergy => allergy.tag).join(', ')}</p>`;
};

const generateSpecialInstructions = ({ special_instructions }) => {
  if (!special_instructions) return `<p>Special Instructions:`;
  return `<p>Special Instructions:<br>${special_instructions}</p>`;
};

const generateServingSize = service => {
  if (['weekly', 'custom_weekly'].includes(service.service_type))
    return service.portion_size
      ? `<p>Portion Size: ${portionSizeSwitch(service.portion_size)}</p>`
      : '';

  let copy = '';
  copy += service.occasion_type
    ? `<p>Occasion Type: ${service.occasion_type}</p>`
    : '';
  copy += service.occasion_type
    ? `<p>Number of Guests: ${service.guest_number}</p>`
    : '';
  return copy;
};

const generateAddress = address => {
  if (!address) return '';
  const street_address = address.street_address_continued
    ? `${address.street_address} ${address.street_address_continued}`
    : address.street_address;
  return `${street_address}, ${address.city}, ${address.state} ${address.zip_code}`;
};

const generateUser = (audience, service) => {
  let copy = `<p><strong>${
    audience === 'staff' ? 'Client' : 'Chef'
  }</strong></p>`;

  if (audience === 'staff')
    return copy + generateUserInfo('Client', service.client);

  const chefs = service.invited_chefs
    ? service.invited_chefs.filter(chef => chef.status === 'accepted')
    : [];
  if (chefs.length > 0)
    return (
      copy +
      chefs
        .map(({ chef }) => generateUserInfo('Chef', chef))
        .join('<p><br></p>')
    );

  return copy + '<p>N/A</p>';
};

const generateUserInfo = (label, user) => {
  let phone = user.phone_number.replace(/\+?1?(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
  return user
    ? `<div><p>${user.name}</p><p>${phone}</p><p>${user.email}</p></div>`
    : '';
};

const generateClientServiceConfirmationIntro = (service) => {
  // Original Copy
  // return '<p>Another week of healthy, well-balanced meals on your table.</p>' +
  //        '<p>Your service is confirmed so you’re golden!</p>' +
  //        '<p>Let’s make sure we have everything right: please look over your service details below and make any last minute changes you’d like.</p>' +
  //        '<p>We are communicating all information to your chef who will be shopping and cooking just for you, so please make these adjustments no later than 24 hours before a service.</p>' +
  //        '<br>' +
  //        '<p>xJill & Tiana</p>';

  const clientName = service.client && service.client.first_name
    ? service.client.first_name : '';

  const serviceStartTime = new Date(service.start_time);
  const serviceDayOfWeek = dayOfWeek(serviceStartTime);
  const oneDayBeforeService = dayOfWeek(subDays(serviceStartTime, 1));

  let chefs = acceptedChefs(service);
  const yourChef = chefs[0] ? chefs[0].chef.first_name : 'Your chef';
  // Launch Copy
  return (
    `<p>Hello ${clientName},</p>` +
    ( !isOccasionService(service) ?
      '<p>Your upcoming service is confirmed! Another week of healthy, well­-balanced meals is coming your way.</p>' +
      `<p>We want to make sure we have everything right, so please look over the details below and let us know if you’d like to make any changes before ${oneDayBeforeService} so your chef has enough time to shop and prepare.</p>` +
      "<p>Please check out our <a href='https://www.theculinistas.com/best-practices/'>Best Practices</a> for additional information about your service, and let us know if you have any questions.</p>" +
      '<p>Cheers to a week of delicious food!</p>' :

      `<p>Your service is confirmed! ${yourChef} is looking forward to cooking for you on ${serviceDayOfWeek}. ` +
      'Please look over the details below and let us know if you’d like to make any changes ' +
      `before ${oneDayBeforeService} so your chef has enough time to shop and prepare.</p>` +
      "<p>Please check out our <a href='https://www.theculinistas.com/best-practices/'>Best Practices</a> for additional information about your service, and let us know if you have any questions.</p>" +
      `<p>${yourChef} will be in touch soon to let you know what time they’ll arrive on ${serviceDayOfWeek} to start cooking.</p>` +
      '<p>Cheers to a great gathering!</p>'
    )
  );
};

const dayOfWeek = date => {
  var weekday = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];
  return weekday[date.getDay()];
};

const acceptedChefs = service => {
  return (service["invited_chefs"] || []).filter(chef => chef.status === "accepted");
};

const generateChefServiceConfirmationIntro = service => {
  // Original Copy
  // return '<p>You have a new client!</p>' +
  //   '<p>So much of this job is about being genuinely connected. Please familiarize yourself with their information so you know who you’re cooking for and please contact  us if you have any questions</p>' +
  //   '<br>' +
  //   '<p>xJill & Tiana</p>';

  // get chef(s) that has/have accepted
  let chefs = acceptedChefs(service);

  const firstName = service.client && service.client.first_name
    ? service.client.first_name
    : 'your client';

  const serviceDayOfWeek = dayOfWeek(new Date(service.start_time));

  // Launch Copy
  return (
    (chefs.length > 0 ? '<p>Hello ' + chefs.map(c => c.chef.first_name).join(', ') + ',</p>' : '<p>Hello!</p>') + 
    `<p>You’re booked with ${firstName} on ${serviceDayOfWeek}!</p>` +
    '<p>Please familiarize yourself with the information for your service below. Have fun (and make some delicious food)! Contact us if you have any questions.</p>' +
    `<p>We always want to see the amazing dishes our chefs are creating, so please tag @theculinistas if you post on IG, or send pics to us by email!</p>` +
    ( 
      isOccasionService(service) ? 
        `<p>IMPORTANT:, please reach out to ${firstName} as soon as you can to introduce yourself and let them know when they can expect you to arrive on ${serviceDayOfWeek} to start cooking!</p>` : ''
    )
  );
};

const generateClientNoteForOccasions = service => {
  return (
    '<p><strong>NOTE ON TIPPING:</strong></p>' + 
    '<p>A minimum tip— 20% of the chef service fee—has been added to your service cost. ' + 
    'If you’d like to provide an additional tip to the chef for extraordinary service, or tip your waitstaff (if present)' +
    `you’re welcome to do so in person, or by asking us to adjust the invoice once received.</p>`
  );
};

const generateChefNoteForOccasions = service => {
  return (
    '<p><strong>NOTE:</strong></p>' + 
    '<p>We’ve let clients know that a minimum tip has been added to their bill ' + 
    'and that they are welcome to provide an additional tip if they would like. ' +
    'To prevent any confusion, if the client offers you a tip, please remind them ' + 
    'that gratuity is already built into the cost of service before accepting the additional tip.</p>'
  );
};




// WEBPACK FOOTER //
// ./src/helpers/emailGenerators.js